import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { brandCssVariables, brandingDefaults } from '../brand/brandSystem';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(brandingDefaults);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  useEffect(() => {
    // Apply branding colors as CSS variables
    applyBranding();
  }, [branding]);

  const resolveBrandingUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return rawUrl;
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;

    const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
    if (!base) return rawUrl;

    if (rawUrl.startsWith('/api/') || rawUrl.startsWith('/uploads/')) {
      return `${base}${rawUrl}`;
    }

    if (rawUrl.startsWith('api/') || rawUrl.startsWith('uploads/')) {
      return `${base}/${rawUrl}`;
    }

    return rawUrl;
  };

  const fetchBranding = async () => {
    try {
      const response = await api.get('/api/branding');
      setBranding({
        ...response.data,
        logo_url: resolveBrandingUrl(response.data.logo_url)
      });
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyBranding = () => {
    const root = document.documentElement;

    Object.entries(brandCssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Apply Bootstrap CSS variables
    root.style.setProperty('--bs-primary', branding.primary_color);
    root.style.setProperty('--bs-primary-rgb', hexToRgb(branding.primary_color));
    root.style.setProperty('--bs-secondary', branding.secondary_color);
    root.style.setProperty('--bs-secondary-rgb', hexToRgb(branding.secondary_color));
    root.style.setProperty('--bs-success', branding.success_color);
    root.style.setProperty('--bs-success-rgb', hexToRgb(branding.success_color));
    root.style.setProperty('--bs-danger', branding.danger_color);
    root.style.setProperty('--bs-danger-rgb', hexToRgb(branding.danger_color));
    root.style.setProperty('--bs-warning', branding.warning_color);
    root.style.setProperty('--bs-warning-rgb', hexToRgb(branding.warning_color));
    root.style.setProperty('--bs-info', branding.info_color);
    root.style.setProperty('--bs-info-rgb', hexToRgb(branding.info_color));
    root.style.setProperty('--bs-body-bg', branding.background_color);
    root.style.setProperty('--bs-body-color', branding.text_color);

    // Apply custom brand CSS variables used throughout the app
    root.style.setProperty('--brand-ink', branding.text_color);
    root.style.setProperty('--brand-ink-soft', lightenColor(branding.text_color, 20));
    root.style.setProperty('--brand-cream', branding.background_color);
    root.style.setProperty('--brand-sand', darkenColor(branding.background_color, 5));
    root.style.setProperty('--brand-accent', branding.primary_color);
    root.style.setProperty('--brand-accent-strong', darkenColor(branding.primary_color, 15));
    root.style.setProperty('--brand-leaf', branding.success_color);
    root.style.setProperty('--brand-secondary', branding.secondary_color);
    root.style.setProperty('--brand-danger', branding.danger_color);
    root.style.setProperty('--brand-warning', branding.warning_color);
    root.style.setProperty('--brand-info', branding.info_color);
    
    // Calculate shadow color from text color with opacity
    const textRgb = hexToRgb(branding.text_color);
    root.style.setProperty('--brand-shadow', `rgba(${textRgb}, 0.16)`);

    // Update body background and text
    document.body.style.backgroundColor = branding.background_color;
    document.body.style.color = branding.text_color;
  };

  const hexToRgb = (hex) => {
    // Handle undefined/null values
    if (!hex) return '0, 0, 0';
    
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  };

  const darkenColor = (hex, percent) => {
    hex = hex.replace('#', '');
    const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - Math.round(255 * percent / 100));
    const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - Math.round(255 * percent / 100));
    const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - Math.round(255 * percent / 100));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const lightenColor = (hex, percent) => {
    hex = hex.replace('#', '');
    const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + Math.round(255 * percent / 100));
    const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + Math.round(255 * percent / 100));
    const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + Math.round(255 * percent / 100));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const refreshBranding = async () => {
    await fetchBranding();
  };

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
